/**
 * Firebase Authentication Service
 *
 * Provides Firebase Auth methods for Python backend integration.
 * Uses Firebase Auth SDK for authentication operations.
 *
 * @see https://firebase.google.com/docs/auth/web/start
 */

import {
    type ActionCodeSettings,
    type AuthError,
    getRedirectResult,
    GithubAuthProvider,
    GoogleAuthProvider,
    isSignInWithEmailLink,
    onAuthStateChanged,
    sendSignInLinkToEmail,
    signInWithEmailLink,
    signInWithPopup,
    signInWithRedirect,
    signOut as firebaseSignOut,
    type User,
} from 'firebase/auth';
import {getFirebaseAuth, isFirebaseConfigured} from '@/shared/config/firebase';
import {getBaseUrl} from '@/shared/config/environment';
import {identifyUser, resetUser, trackSignIn, trackSignOut, trackSignUp} from '@/shared/utils/mixpanel';
import {logger} from '@/shared/lib/logger/logger';
import type {AuthSession, FirebaseAuthServiceResponse} from '../types';

/**
 * Firebase Authentication Service
 *
 * Provides authentication methods using Firebase Auth SDK.
 */
export class FirebaseAuthService {
  private readonly EMAIL_STORAGE_KEY = 'firebase_email_for_signin';

  /**
   * Check if Firebase is configured
   */
  isConfigured(): boolean {
    return isFirebaseConfigured();
  }

  /**
   * Send magic link for passwordless authentication
   *
   * Firebase Auth sends an email with a sign-in link.
   * The user clicks the link to complete authentication.
   *
   * Uses full actionCodeSettings according to Firebase docs:
   * https://firebase.google.com/docs/auth/web/email-link-auth
   *
   * @param email - User email address
   * @param redirectTo - Optional redirect URL after sign-in
   * @returns Promise with success/error
   */
  async sendMagicLink(
    email: string,
    redirectTo?: string
  ): Promise<FirebaseAuthServiceResponse> {
    try {
      if (!this.isConfigured()) {
        throw new Error('Firebase is not configured');
      }

      const auth = getFirebaseAuth();
      const baseUrl = getBaseUrl();
      const callbackUrl = redirectTo || `${baseUrl}/auth/callback`;

      // Full actionCodeSettings according to Firebase docs
      const actionCodeSettings: ActionCodeSettings = {
        // URL you want to redirect back to. The domain must be in authorized domains list
        url: callbackUrl,
        // This must be true for email link authentication
        handleCodeInApp: true,
      };

      // Save email for later use
      window.localStorage.setItem(this.EMAIL_STORAGE_KEY, email);

      await sendSignInLinkToEmail(auth, email, actionCodeSettings);

      // Track magic link request
      trackSignUp('magic_link');

      return { data: undefined };
    } catch (error) {
      logger.error('Firebase magic link error', error instanceof Error ? error : new Error(String(error)), {
        component: 'FirebaseAuthService',
        action: 'send_magic_link',
        email,
      });
      return { error: error as AuthError | Error };
    }
  }

  /**
   * Sign in with email link (magic link)
   *
   * Call this method when user clicks the magic link in their email.
   * Follows Firebase docs: https://firebase.google.com/docs/auth/web/email-link-auth
   *
   * @param email - User email address (optional, will try to get from localStorage if not provided)
   * @param emailLink - The magic link URL from email (defaults to window.location.href)
   * @returns Promise with user/error
   */
  async signInWithEmailLink(
    email?: string,
    emailLink?: string
  ): Promise<FirebaseAuthServiceResponse<User>> {
    try {
      if (!this.isConfigured()) {
        throw new Error('Firebase is not configured');
      }

      const auth = getFirebaseAuth();
      const link = emailLink || window.location.href;
      const emailToUse = email || this.getSavedEmail();

      if (!emailToUse) {
        throw new Error('Email not found. Please request a new magic link.');
      }

      // Verify the link is valid according to Firebase docs
      if (!isSignInWithEmailLink(auth, link)) {
        throw new Error('Invalid sign-in link. Please request a new magic link.');
      }

      // Sign in with the email link
      const userCredential = await signInWithEmailLink(auth, emailToUse, link);

      // Clear stored email
      window.localStorage.removeItem(this.EMAIL_STORAGE_KEY);

      // Track sign-in
      const user = userCredential.user;
      trackSignIn('magic_link', user.uid);
      identifyUser(user.uid, {
        email: user.email || '',
        createdAt: user.metadata.creationTime || new Date().toISOString(),
      });

      return { data: user };
    } catch (error) {
      logger.error('Firebase sign-in error', error instanceof Error ? error : new Error(String(error)), {
        component: 'FirebaseAuthService',
        action: 'sign_in_with_email_link',
        email,
      });
      return { error: error as AuthError | Error };
    }
  }

  /**
   * Get current user
   *
   * @returns Current Firebase user or null
   */
  getCurrentUser(): User | null {
    try {
      if (!this.isConfigured()) {
        return null;
      }

      const auth = getFirebaseAuth();
      return auth.currentUser;
    } catch (error) {
      logger.error('Get current user error', error instanceof Error ? error : new Error(String(error)), {
        component: 'FirebaseAuthService',
        action: 'get_current_user',
      });
      return null;
    }
  }

  /**
   * Get Firebase ID token
   *
   * Returns the ID token for the current user, which can be used
   * to authenticate API requests to Python backend.
   *
   * @param forceRefresh - Force token refresh
   * @returns ID token or null
   */
  async getIdToken(forceRefresh = false): Promise<string | null> {
    try {
      if (!this.isConfigured()) {
        return null;
      }

      const auth = getFirebaseAuth();
      const user = auth.currentUser;

      if (!user) {
        return null;
      }

      return await user.getIdToken(forceRefresh);
    } catch (error) {
      logger.error('Get ID token error', error instanceof Error ? error : new Error(String(error)), {
        component: 'FirebaseAuthService',
        action: 'get_id_token',
      });
      return null;
    }
  }

  /**
   * Get current session (compatibility with legacy auth interface)
   *
   * Returns a session-like object with user and access_token.
   *
   * @returns Session object or null
   */
  async getCurrentSession(): Promise<AuthSession | null> {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        return null;
      }

      const token = await this.getIdToken();
      if (!token) {
        return null;
      }

      return {
        user,
        access_token: token,
      };
    } catch (error) {
      logger.error('Get current session error', error instanceof Error ? error : new Error(String(error)), {
        component: 'FirebaseAuthService',
        action: 'get_current_session',
      });
      return null;
    }
  }

  /**
   * Check if user is authenticated
   *
   * Waits for Firebase Auth to be ready and checks if user is logged in.
   * Firebase Auth loads user from indexedDB asynchronously, so we need to wait.
   *
   * @returns True if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      if (!this.isConfigured()) {
        return false;
      }

      const auth = getFirebaseAuth();

      // Wait for auth state to be ready (Firebase loads from indexedDB asynchronously)
      return new Promise<boolean>((resolve) => {
        // Check currentUser first (may be available immediately)
        if (auth.currentUser) {
          resolve(true);
          return;
        }

        // Wait for auth state to be determined
        const unsubscribe = auth.onAuthStateChanged((user) => {
          unsubscribe(); // Unsubscribe after first state change
          resolve(!!user);
        });

        // Timeout after 2 seconds if auth state doesn't change
        setTimeout(() => {
          unsubscribe();
          // Fallback: check currentUser directly
          resolve(!!auth.currentUser);
        }, 2000);
      });
    } catch (error) {
      logger.error('Check authentication error', error instanceof Error ? error : new Error(String(error)), {
        component: 'FirebaseAuthService',
        action: 'is_authenticated',
      });
      return false;
    }
  }

  /**
   * Subscribe to auth state changes
   *
   * @param callback - Callback function for auth events
   * @returns Unsubscribe function
   */
  onAuthStateChange(
    callback: (event: string, session: AuthSession | null) => void
  ): () => void {
    if (!this.isConfigured()) {
      // Return no-op unsubscribe if Firebase not configured
      return () => { /* Intentionally empty */ };
    }

    const auth = getFirebaseAuth();

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User signed in
        void user.getIdToken().then((token) => {
          if (token) {
            trackSignIn('session_restored', user.uid);
            identifyUser(user.uid, {
              email: user.email || '',
              createdAt: user.metadata.creationTime || new Date().toISOString(),
            });
            callback('SIGNED_IN', { user, access_token: token });
          }
        }).catch(() => {
          // Token fetch failed, ignore
        });
      } else {
        // User signed out
        trackSignOut();
        resetUser();
        callback('SIGNED_OUT', null);
      }
    });

    return unsubscribe;
  }

  /**
   * Sign out user
   *
   * @returns Promise with success/error
   */
  async signOut(): Promise<FirebaseAuthServiceResponse> {
    try {
      if (!this.isConfigured()) {
        return { error: new Error('Firebase is not configured') };
      }

      const auth = getFirebaseAuth();
      await firebaseSignOut(auth);

      // Clear local storage
      this.clearLocalStorage();

      // Track sign-out and reset Mixpanel user
      trackSignOut();
      resetUser();

      return { data: undefined };
    } catch (error) {
      logger.error('Sign out error', error instanceof Error ? error : new Error(String(error)), {
        component: 'FirebaseAuthService',
        action: 'sign_out',
      });
      return { error: error as AuthError | Error };
    }
  }

  /**
   * Check if URL is an email sign-in link
   *
   * @param url - URL to check (defaults to current location)
   * @returns True if the URL is a valid email sign-in link
   */
  isEmailLink(url?: string): boolean {
    try {
      if (!this.isConfigured()) {
        return false;
      }

      const auth = getFirebaseAuth();
      const linkToCheck = url || window.location.href;
      return isSignInWithEmailLink(auth, linkToCheck);
    } catch (error) {
      logger.error('Check email link error', error instanceof Error ? error : new Error(String(error)), {
        component: 'FirebaseAuthService',
        action: 'check_email_link',
      });
      return false;
    }
  }

  /**
   * Get saved email from localStorage
   */
  getSavedEmail(): string | null {
    return window.localStorage.getItem(this.EMAIL_STORAGE_KEY);
  }

  /**
   * Save email to localStorage
   */
  saveEmailToLocalStorage(email: string): void {
    window.localStorage.setItem(this.EMAIL_STORAGE_KEY, email);
  }

  /**
   * Sign in with Google using Firebase Auth
   *
   * Uses Firebase GoogleAuthProvider for OAuth sign-in.
   *
   * @param useRedirect - If true, uses redirect flow (better for mobile). If false, uses popup (default).
   * @returns Promise with user/error
   */
  async signInWithGoogle(useRedirect = false): Promise<FirebaseAuthServiceResponse<User>> {
    try {
      if (!this.isConfigured()) {
        throw new Error('Firebase is not configured');
      }

      const auth = getFirebaseAuth();
      const provider = new GoogleAuthProvider();

      // Add scopes if needed
      provider.addScope('profile');
      provider.addScope('email');

      let userCredential: { user: User } | undefined;
      if (useRedirect) {
        // Redirect flow (better for mobile, handles redirect automatically)
        await signInWithRedirect(auth, provider);
        // User will be redirected, so we return undefined
        return {};
      } else {
        // Popup flow (better UX for desktop)
        userCredential = await signInWithPopup(auth, provider);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Runtime safety check
      if (!userCredential) {
        return {};
        }
      }

      const user = userCredential.user;
      trackSignIn('google', user.uid);
      identifyUser(user.uid, {
        email: user.email || '',
        createdAt: user.metadata.creationTime || new Date().toISOString(),
      });

      return { data: user };
    } catch (error) {
      const authError = error as AuthError;
      // Handle popup closed by user - not a real error
      if (authError.code === 'auth/popup-closed-by-user' || authError.code === 'auth/cancelled-popup-request') {
        return {};
      }
      logger.error('Firebase Google sign-in error', authError, {
        component: 'FirebaseAuthService',
        action: 'sign_in_with_google',
      });
      return { error: authError };
    }
  }

  /**
   * Sign in with GitHub using Firebase Auth
   *
   * Uses Firebase GithubAuthProvider for OAuth sign-in.
   *
   * @param useRedirect - If true, uses redirect flow (better for mobile). If false, uses popup (default).
   * @returns Promise with user/error
   */
  async signInWithGithub(useRedirect = false): Promise<FirebaseAuthServiceResponse<User>> {
    try {
      if (!this.isConfigured()) {
        throw new Error('Firebase is not configured');
      }

      const auth = getFirebaseAuth();
      const provider = new GithubAuthProvider();

      // Add scopes if needed
      provider.addScope('read:user');

      let userCredential: { user: User } | undefined;
      if (useRedirect) {
        // Redirect flow (better for mobile, handles redirect automatically)
        await signInWithRedirect(auth, provider);
        // User will be redirected, so we return undefined
        return {};
      } else {
        // Popup flow (better UX for desktop)
        userCredential = await signInWithPopup(auth, provider);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Runtime safety check
      if (!userCredential) {
        return {};
        }
      }

      const user = userCredential.user;
      trackSignIn('github', user.uid);
      identifyUser(user.uid, {
        email: user.email || '',
        createdAt: user.metadata.creationTime || new Date().toISOString(),
      });

      return { data: user };
    } catch (error) {
      const authError = error as AuthError;
      // Handle popup closed by user - not a real error
      if (authError.code === 'auth/popup-closed-by-user' || authError.code === 'auth/cancelled-popup-request') {
        return {};
      }
      logger.error('Firebase GitHub sign-in error', authError, {
        component: 'FirebaseAuthService',
        action: 'sign_in_with_github',
      });
      return { error: authError };
    }
  }

  /**
   * Handle OAuth redirect result
   *
   * Completes the OAuth redirect flow to get the user credential.
   * Returns undefined if no redirect result (normal when user didn't come from OAuth redirect).
   *
   * @returns Promise with user/error/undefined
   */
  async getRedirectResult(): Promise<FirebaseAuthServiceResponse<User> | { data: undefined }> {
    try {
      if (!this.isConfigured()) {
        return { data: undefined };
      }

      const auth = getFirebaseAuth();
      const result = await getRedirectResult(auth);

      if (!result) {
        // No redirect result (user didn't come from OAuth redirect) - this is normal
        return { data: undefined };
      }

      const user = result.user;
      trackSignIn('oauth_redirect', user.uid);
      identifyUser(user.uid, {
        email: user.email || '',
        createdAt: user.metadata.creationTime || new Date().toISOString(),
      });

      return { data: user };
    } catch (error) {
      // If error is auth/popup-closed-by-user or similar, it's not a real error
      const authError = error as AuthError;
      if (authError.code === 'auth/popup-closed-by-user' || authError.code === 'auth/cancelled-popup-request') {
        return { data: undefined };
      }
      logger.error('Firebase redirect result error', error instanceof Error ? error : new Error(String(error)), {
        component: 'FirebaseAuthService',
        action: 'get_redirect_result',
      });
      return { error: error as AuthError | Error };
    }
  }

  /**
   * Clear localStorage
   */
  private clearLocalStorage(): void {
    window.localStorage.removeItem(this.EMAIL_STORAGE_KEY);
  }
}

// Export singleton instance
export const firebaseAuthService = new FirebaseAuthService();
